import {ApiClient as Api} from '@hconnect/apiclient'

import type {SortDirection} from '../helpers'
import type {
  AuditLogEntryDto,
  CommentDto,
  PaginatedResponse,
  ReportLinkDto,
  ReportAuditLogEntryDto,
  ReportSummaryDto,
  ImportDuplicateCheckRequest,
  ImportDuplicateCheckResponseDto,
  RiaDetailDto,
  RiaDetailsAuditLogEntryDto,
  RiaListResponse,
  RiaQueryFilter,
  UserSuggestionDto
} from '../types'

export const riskSurveyQueries = {
  getReports: async (
    params: {
      upmId?: string
      page?: number
      pageSize?: number
      includeArchived?: boolean
      sortBy?: string
      sortDir?: SortDirection
    },
    signal?: AbortSignal
  ) => {
    const response = await Api.axiosInstance.get<PaginatedResponse<ReportSummaryDto>>(
      '/risk-survey/reports',
      {params, signal}
    )
    return response.data
  },

  getReportDetail: async (reportId: string, signal?: AbortSignal) => {
    const response = await Api.axiosInstance.get<ReportSummaryDto>(
      `/risk-survey/reports/${encodeURIComponent(reportId)}`,
      {signal}
    )
    return response.data
  },

  checkImportDuplicate: async (data: ImportDuplicateCheckRequest, signal?: AbortSignal) => {
    const response = await Api.axiosInstance.post<ImportDuplicateCheckResponseDto>(
      '/risk-survey/reports/import-check',
      data,
      {signal}
    )
    return response.data
  },

  getRias: async (filter: RiaQueryFilter, signal?: AbortSignal) => {
    const paramsSerializer = (params: RiaQueryFilter) => {
      const searchParams = new URLSearchParams()

      if (params.reportId) searchParams.append('reportId', params.reportId)
      if (params.upmId) searchParams.append('upmId', params.upmId)
      if (params.search) searchParams.append('search', params.search)
      if (params.sortBy) searchParams.append('sortBy', params.sortBy)
      if (params.sortDir) searchParams.append('sortDir', params.sortDir)

      searchParams.append('page', String(params.page))
      searchParams.append('pageSize', String(params.pageSize))

      params.status?.forEach((status) => searchParams.append('status', status))
      params.priority?.forEach((priority) => searchParams.append('priority', priority))

      return searchParams.toString()
    }

    const response = await Api.axiosInstance.get<RiaListResponse>('/risk-survey/rias', {
      params: filter,
      paramsSerializer,
      signal
    })
    return response.data
  },

  getRiaDetail: async (riaId: string, signal?: AbortSignal) => {
    const response = await Api.axiosInstance.get<RiaDetailDto>(
      `/risk-survey/rias/${encodeURIComponent(riaId)}`,
      {signal}
    )
    return response.data
  },

  getRiaComments: async (riaId: string, page: number, pageSize: number, signal?: AbortSignal) => {
    const response = await Api.axiosInstance.get<PaginatedResponse<CommentDto>>(
      `/risk-survey/rias/${encodeURIComponent(riaId)}/comments`,
      {params: {page, pageSize}, signal}
    )
    return response.data
  },

  getRiaAuditLog: async (riaId: string, signal?: AbortSignal) => {
    const response = await Api.axiosInstance.get<AuditLogEntryDto[]>(
      `/risk-survey/rias/${encodeURIComponent(riaId)}/audit-log`,
      {signal}
    )
    return response.data
  },

  getRiaDetailsAuditLog: async (riaId: string, signal?: AbortSignal) => {
    const response = await Api.axiosInstance.get<RiaDetailsAuditLogEntryDto[]>(
      `/risk-survey/rias/${encodeURIComponent(riaId)}/details-audit-log`,
      {signal}
    )
    return response.data
  },

  exportReport: async (reportId: string) => {
    const response = await Api.axiosInstance.get(
      `/risk-survey/reports/${encodeURIComponent(reportId)}/export`,
      {responseType: 'arraybuffer'}
    )
    return new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
  },

  getReportAuditLog: async (reportId: string, signal?: AbortSignal) => {
    const response = await Api.axiosInstance.get<ReportAuditLogEntryDto[]>(
      `/risk-survey/reports/${encodeURIComponent(reportId)}/audit-log`,
      {signal}
    )
    return response.data
  },

  getReportLinks: async (reportId: string, signal?: AbortSignal) => {
    const response = await Api.axiosInstance.get<ReportLinkDto[]>(
      `/risk-survey/reports/${encodeURIComponent(reportId)}/links`,
      {signal}
    )
    return response.data
  },

  getUserSuggestions: async (upmId: string, namePattern: string, signal?: AbortSignal) => {
    const response = await Api.axiosInstance.get<UserSuggestionDto[]>(
      `/risk-survey/plants/${encodeURIComponent(upmId)}/user-suggestions`,
      {params: {namePattern}, signal}
    )
    return response.data
  }
}
